import { getNameFromModuleDefinition } from "src/stores/TemplateStore";
import { getNameFromFlexComponent } from "src/stores/CompositionStore";
import { Template, Composition } from "src/typings/flexFramework/FlexDefinitions";
import { ModuleDefinition, FlexComponent } from "src/typings/flexFramework/FlexComponent";
import { flexComponentMap } from "src/components/flexFramework/FlexComponents/flexComponentMap";
import { FlexComponentMapOptions } from "../FlexComponents/typings";

export interface BlossomCodeSplitterProps {
  template?: Template;
  composition?: Composition;
}

export class BlossomCodeSplitter {
  /**
   * Returns a de-duped array of entries from flexComponentMap that correspond to the provided Flex Template or Composition. If no template or composition is provided, all entries will be returned.
   * @param input
   * @returns FlexComponentMapOptions[]
   */
  public getRequiredCodeSplitBundles = (input: BlossomCodeSplitterProps) => {
    const { template, composition } = input;
    const requiredCodeSplitBundles = new Set<FlexComponentMapOptions>();

    const collectChildren = (node: ModuleDefinition | FlexComponent) => [
      ...(node.modules || []),
      ...(node.groups || []),
      ...(node.children || []),
    ];

    if (template) {
      const reducer = (acc: Set<FlexComponentMapOptions>, moduleDefinition: ModuleDefinition) => {
        const componentName = getNameFromModuleDefinition(moduleDefinition);
        const componentMapping =
          flexComponentMap.get(`${componentName}_${moduleDefinition.view}`) || flexComponentMap.get(componentName);
        if (!componentMapping) {
          return acc;
        }

        acc.add(componentMapping);

        collectChildren(moduleDefinition).forEach((child) => {
          reducer(acc, child);
        });

        return acc;
      };

      return [...template.regions.reduce(reducer, requiredCodeSplitBundles)];
    } else if (composition) {
      const reducer = (acc: Set<FlexComponentMapOptions>, flexComponent: FlexComponent) => {
        const componentName = getNameFromFlexComponent(flexComponent);
        const componentMapping =
          flexComponentMap.get(`${componentName}_${flexComponent.model?.view}`) || flexComponentMap.get(componentName);
        if (!componentMapping) {
          return acc;
        }

        acc.add(componentMapping);

        collectChildren(flexComponent).forEach((child) => {
          reducer(acc, child);
        });

        return acc;
      };

      return [...composition.page.regionList.reduce(reducer, requiredCodeSplitBundles)];
    } else {
      return [...flexComponentMap.values()];
    }
  };

  /**
   * returns a deduped array of webpackChunkNames of the entries in flexComponentMap that correspond to the provided Flex Template or Composition. If no template or composition is provided, all webpackChunkNames from flexComponentMap will be returned
   * @param input
   * @returns string[]
   */
  public getRequiredWebpackChunkNames = (input: BlossomCodeSplitterProps) =>
    this.getRequiredCodeSplitBundles(input).map((bundle) => bundle.webpackChunkName);

  /**
   * loads webpack code split chunks required to render items in the provided Flex Template or Composition. If no template or composition is provided, all webpack code split chunks will be loaded.
   * @param input
   * @returns Promise
   */
  public loadRequiredCodeSplitBundles = (input: BlossomCodeSplitterProps) => {
    const requiredCodeSplitBundles = this.getRequiredCodeSplitBundles(input)
      .filter((bundle) => bundle?.codeSplit && typeof (bundle.codeSplit as any).loadRoute === "function")
      .map((bundle) => bundle && (bundle.codeSplit as any).loadRoute());

    return Promise.all(requiredCodeSplitBundles);
  };
}
